import type { ApiKey } from '../types/api'
import { formatDate } from './format'
import { secretLabel, type KeyChain } from './keyChains'

export interface DeleteOutcome {
  deleted: string[]
  failed: { id: string; message: string }[]
}

const FALLBACK_MESSAGE = 'Failed to delete. Please try again.'

/** One at a time: secrets on one Graph app conflict when written together. A 404 means the secret is not on the app. */
export async function deleteSecrets(ids: string[], remove: (id: string) => Promise<void>): Promise<DeleteOutcome> {
  const outcome: DeleteOutcome = { deleted: [], failed: [] }
  for (const id of ids) {
    try {
      await remove(id)
      outcome.deleted.push(id)
    } catch (error) {
      if ((error as { status?: number } | null)?.status === 404) {
        outcome.deleted.push(id)
      } else {
        const message = error instanceof Error && error.message ? error.message : FALLBACK_MESSAGE
        outcome.failed.push({ id, message })
      }
    }
  }
  return outcome
}

function listed(labels: string[]): string {
  return labels.length > 1 ? `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}` : labels[0]
}

export function chainDeleteConfirmation(chain: KeyChain): string {
  if (chain.secrets.length === 1) {
    return 'Are you sure you want to permanently delete this API key? This cannot be undone.'
  }
  const working = chain.secrets.filter((s) => s.status === 'active')
  const head = `Delete ${chain.current.display_name} and its ${chain.secrets.length} secrets?`
  if (working.length === 0) return `${head} This cannot be undone.`
  const one = working.length === 1
  const names = listed(working.map((s) => `${secretLabel(s)} until ${formatDate(s.expires_at)}`))
  return (
    `${head} ${working.length} still ${one ? 'works' : 'work'}: ${names}. ` +
    `Anything that uses ${one ? 'it' : 'them'} stops working. This cannot be undone.`
  )
}

export function secretDeleteConfirmation(secret: ApiKey): string {
  const label = secretLabel(secret)
  return secret.status === 'active'
    ? `Delete secret ${label}? It works until ${formatDate(secret.expires_at)}, and anything that uses it ` +
        'stops working. This cannot be undone.'
    : `Delete secret ${label}? It expired on ${formatDate(secret.expires_at)}. This cannot be undone.`
}

/** The failures among a key's secrets; undefined while none failed. */
export function deleteErrorText(chain: KeyChain, deleting: Map<string, string | null>): string | undefined {
  const failures = chain.secrets
    .filter((s) => deleting.get(s.id))
    .map((s) => `${secretLabel(s)} could not be deleted: ${deleting.get(s.id)}`)
  return failures.length ? failures.join('; ') : undefined
}
